import { ConstructionPhase } from '@antification/core';
import { ParkingtonTool, type ToolDefinition, type ParkingtonState } from '../ParkingtonTypes';

/**
 * Context needed for canvas UI rendering.
 */
export interface CanvasUIRenderContext {
  state: ParkingtonState;
  isPointInRect: (x: number, y: number, rect: { x: number; y: number; w: number; h: number }) => boolean;
  formatTimeAgo: (timestamp: number) => string;
}

/**
 * Renders legacy canvas UI panels: save slots, tool selector, header.
 */
export class CanvasUIRenderer {
  // Mutable areas set during render for hit-testing
  saveButtonArea: { x: number; y: number; w: number; h: number } | null = null;
  loadButtonArea: { x: number; y: number; w: number; h: number } | null = null;
  saveSlotAreas: Array<{ x: number; y: number; w: number; h: number }> = [];
  toolButtonAreas: Array<{ x: number; y: number; w: number; h: number; tool: ParkingtonTool }> = [];

  /**
   * Get phase display label.
   */
  getPhaseLabel(phase: ConstructionPhase): string {
    const labels: Record<ConstructionPhase, string> = {
      [ConstructionPhase.SURVEY]: 'Surveying Land',
      [ConstructionPhase.CLEAR]: 'Clearing Vegetation',
      [ConstructionPhase.GRADE]: 'Grading Terrain',
      [ConstructionPhase.POUR]: 'Pouring Concrete',
      [ConstructionPhase.CURE]: 'Concrete Curing',
      [ConstructionPhase.PAINT]: 'Painting Lines',
      [ConstructionPhase.COMPLETE]: 'PROJECT COMPLETE!',
    };
    return labels[phase];
  }

  /**
   * Render the save slot picker dropdown.
   */
  renderSaveSlotPicker(
    ctx: CanvasRenderingContext2D,
    rctx: CanvasUIRenderContext,
    x: number,
    y: number,
    panelWidth: number,
    saves: Array<{ timestamp: number; isAutosave?: boolean; name?: string }>
  ): void {
    const slotHeight = 34;
    const padding = 4;
    const totalHeight = saves.length * slotHeight + padding * 2;

    ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
    ctx.strokeStyle = '#428bca';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(x, y, panelWidth, totalHeight, 6);
    ctx.fill();
    ctx.stroke();

    this.saveSlotAreas = [];

    saves.forEach((save, index) => {
      const slotY = y + padding + index * slotHeight;
      const slotArea = { x: x + padding, y: slotY, w: panelWidth - padding * 2, h: slotHeight - 2 };
      this.saveSlotAreas.push(slotArea);

      const isHovered = rctx.isPointInRect(rctx.state.mouseX, rctx.state.mouseY, slotArea);

      ctx.fillStyle = isHovered ? 'rgba(66, 139, 202, 0.2)' : 'transparent';
      ctx.beginPath();
      ctx.roundRect(slotArea.x, slotArea.y, slotArea.w, slotArea.h, 3);
      ctx.fill();

      ctx.fillStyle = '#666';
      ctx.font = 'bold 10px "Segoe UI", system-ui, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${index + 1}.`, slotArea.x + 4, slotArea.y + slotArea.h / 2);

      const name = save.name?.trim() || (save.isAutosave ? 'Autosave' : 'Untitled save');
      ctx.fillStyle = '#333';
      ctx.font = 'bold 11px "Segoe UI", system-ui, sans-serif';
      ctx.fillText(name, slotArea.x + 22, slotArea.y + 12);

      const timeAgo = rctx.formatTimeAgo(save.timestamp);
      ctx.fillStyle = '#666';
      ctx.font = '9px "Segoe UI", system-ui, sans-serif';
      ctx.fillText(timeAgo, slotArea.x + 22, slotArea.y + slotArea.h - 6);

      if (save.isAutosave) {
        ctx.fillStyle = '#888';
        ctx.font = '9px "Segoe UI", system-ui, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText('auto', slotArea.x + slotArea.w - 4, slotArea.y + 12);
      }
    });

    ctx.fillStyle = '#999';
    ctx.font = '9px "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Click to load, or click Load to close', x + panelWidth / 2, y + totalHeight + 10);
  }

  /**
   * Render the header text.
   */
  renderHeader(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#2d5a7b';
    ctx.font = 'bold 18px "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText('PARKINGTON DEVELOPMENT SUITE', 20, 15);

    ctx.font = '12px "Segoe UI", system-ui, sans-serif';
    ctx.fillStyle = '#666';
    ctx.fillText('Every great community starts with convenient parking.', 20, 38);
  }

  /**
   * Render the tool selector panel (legacy canvas version).
   */
  renderToolSelector(ctx: CanvasRenderingContext2D, rctx: CanvasUIRenderContext): void {
    const startX = 20;
    const startY = 60;
    const buttonSize = 40;
    const buttonGap = 6;
    const padding = 8;

    const toolDefs = rctx.state.toolDefinitions;
    const panelWidth = toolDefs.length * (buttonSize + buttonGap) - buttonGap + padding * 2;
    const panelHeight = buttonSize + padding * 2;

    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.strokeStyle = '#ddd';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(startX, startY, panelWidth, panelHeight, 8);
    ctx.fill();
    ctx.stroke();

    this.toolButtonAreas = [];

    toolDefs.forEach((tool, index) => {
      const buttonX = startX + padding + index * (buttonSize + buttonGap);
      const buttonY = startY + padding;

      this.toolButtonAreas.push({
        x: buttonX,
        y: buttonY,
        w: buttonSize,
        h: buttonSize,
        tool: tool.id,
      });

      const isActive = rctx.state.currentTool === tool.id;
      const isHovered = rctx.isPointInRect(rctx.state.mouseX, rctx.state.mouseY, { x: buttonX, y: buttonY, w: buttonSize, h: buttonSize });

      if (!tool.available) {
        ctx.fillStyle = '#f0f0f0';
      } else if (isActive) {
        ctx.fillStyle = '#2d5a7b';
      } else if (isHovered) {
        ctx.fillStyle = '#e0e8ee';
      } else {
        ctx.fillStyle = '#f8f8f8';
      }

      ctx.strokeStyle = isActive ? '#1a3d52' : '#ccc';
      ctx.lineWidth = isActive ? 2 : 1;
      ctx.beginPath();
      ctx.roundRect(buttonX, buttonY, buttonSize, buttonSize, 6);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = !tool.available ? '#aaa' : isActive ? '#fff' : '#333';
      ctx.font = '18px "Segoe UI", system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(tool.icon, buttonX + buttonSize / 2, buttonY + buttonSize / 2 - 4);

      ctx.fillStyle = !tool.available ? '#ccc' : isActive ? 'rgba(255,255,255,0.7)' : '#888';
      ctx.font = '9px "Segoe UI", system-ui, sans-serif';
      ctx.fillText(tool.shortcut, buttonX + buttonSize / 2, buttonY + buttonSize - 6);

      if (tool.id === ParkingtonTool.PRECISION && !tool.available) {
        const progress = Math.min(1, rctx.state.cellsChanged / rctx.state.PRECISION_UNLOCK);
        const barWidth = buttonSize - 8;
        const barHeight = 3;
        const barX = buttonX + 4;
        const barY = buttonY + buttonSize - 10;

        ctx.fillStyle = '#ddd';
        ctx.beginPath();
        ctx.roundRect(barX, barY, barWidth, barHeight, 1);
        ctx.fill();

        if (progress > 0) {
          ctx.fillStyle = '#4caf50';
          ctx.beginPath();
          ctx.roundRect(barX, barY, barWidth * progress, barHeight, 1);
          ctx.fill();
        }
      }
    });

    // Tooltip for hovered tool
    for (const area of this.toolButtonAreas) {
      if (rctx.isPointInRect(rctx.state.mouseX, rctx.state.mouseY, area)) {
        const tool = toolDefs.find(t => t.id === area.tool);
        if (tool) {
          this.renderToolTooltip(ctx, rctx, area.x + buttonSize / 2, area.y + buttonSize + 8, tool);
        }
        break;
      }
    }
  }

  /**
   * Render tooltip for a tool button.
   */
  private renderToolTooltip(
    ctx: CanvasRenderingContext2D,
    rctx: CanvasUIRenderContext,
    x: number,
    y: number,
    tool: ToolDefinition
  ): void {
    const padding = 6;

    let text: string;
    if (tool.available) {
      text = `${tool.name} - ${tool.description}`;
    } else if (tool.id === ParkingtonTool.PRECISION) {
      const remaining = rctx.state.PRECISION_UNLOCK - rctx.state.cellsChanged;
      text = `${tool.name} - Unlocks after ${remaining} more cells developed`;
    } else {
      text = `${tool.name} (Coming Soon) - ${tool.description}`;
    }

    ctx.font = '11px "Segoe UI", system-ui, sans-serif';
    const textWidth = ctx.measureText(text).width;
    const boxWidth = textWidth + padding * 2;
    const boxHeight = 20;

    let tooltipX = x - boxWidth / 2;
    tooltipX = Math.max(10, Math.min(rctx.state.width - boxWidth - 10, tooltipX));

    ctx.fillStyle = 'rgba(50, 50, 50, 0.95)';
    ctx.beginPath();
    ctx.roundRect(tooltipX, y, boxWidth, boxHeight, 4);
    ctx.fill();

    ctx.fillStyle = '#fff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, tooltipX + padding, y + boxHeight / 2);
  }
}
